import { useEffect, useRef } from 'react';
import { fetchSpotPricesFromAPI, fetchFixingPricesFromAPI, fetchHistoryFromAPI } from '../lib/metal-price/api';
import { TimePeriod } from '../lib/metal-price/types';

interface PreloadingStatus {
  spot: boolean;
  fixing: boolean;
  weekly: boolean;
  monthly: boolean;
  yearly: boolean;
  all: boolean;
}

/**
 * Hook per il preloading dei dati sui prezzi dei metalli lato client
 * 
 * Questo hook si attiva automaticamente quando il componente viene montato
 * e precarica tutti i dati necessari per garantire performance ottimali.
 * 
 * Caratteristiche:
 * - Preloading automatico al mount del componente
 * - Esecuzione in parallelo per performance ottimali
 * - Gestione errori robusta
 * - Logging dettagliato per debugging
 * - Prevenzione di preloading multipli
 */
export const useMetalPricePreloader = () => {
  const hasPreloaded = useRef(false);
  const preloadingStatus = useRef<PreloadingStatus>({
    spot: false,
    fixing: false,
    weekly: false,
    monthly: false,
    yearly: false,
    all: false
  });

  useEffect(() => {
    // Previeni preloading multipli
    if (hasPreloaded.current) {
      return;
    }

    const preloadData = async () => {
      console.log('[CLIENT PRELOADER] Avvio preloading dati metalli preziosi...');
      hasPreloaded.current = true;

      const preloadPromises = [];

      // Preload prezzi spot
      preloadPromises.push(
        fetchSpotPricesFromAPI()
          .then(() => {
            preloadingStatus.current.spot = true;
            console.log('[CLIENT PRELOADER] ✅ Prezzi spot precaricati');
          })
          .catch(error => {
            console.warn('[CLIENT PRELOADER] ⚠️ Errore preload prezzi spot:', error.message);
          })
      );

      // Preload prezzi fixing
      preloadPromises.push(
        fetchFixingPricesFromAPI()
          .then(() => {
            preloadingStatus.current.fixing = true;
            console.log('[CLIENT PRELOADER] ✅ Prezzi fixing precaricati');
          })
          .catch(error => {
            console.warn('[CLIENT PRELOADER] ⚠️ Errore preload prezzi fixing:', error.message);
          })
      );

      // Preload dati storici
      const periods: { period: TimePeriod; key: keyof PreloadingStatus }[] = [
        { period: 'week', key: 'weekly' },
        { period: 'month', key: 'monthly' },
        { period: 'year', key: 'yearly' },
        { period: 'all', key: 'all' }
      ];
      
      periods.forEach(({ period, key }) => {
        preloadPromises.push(
          fetchHistoryFromAPI(period)
            .then(() => {
              preloadingStatus.current[key] = true;
              console.log(`[CLIENT PRELOADER] ✅ Dati storici ${key} precaricati`);
            })
            .catch(error => {
              console.warn(`[CLIENT PRELOADER] ⚠️ Errore preload dati storici ${key}:`, error.message);
            })
        );
      });

      // Esegui tutti i preload in parallelo
      try {
        await Promise.allSettled(preloadPromises);
        
        const completedCount = Object.values(preloadingStatus.current).filter(Boolean).length;
        const totalCount = Object.keys(preloadingStatus.current).length;
        
        console.log(`[CLIENT PRELOADER] 🎉 Preloading completato: ${completedCount}/${totalCount} operazioni riuscite`);
        
        if (completedCount === totalCount) {
          console.log('[CLIENT PRELOADER] ✨ Tutti i dati sono stati precaricati con successo!');
        } else {
          console.log('[CLIENT PRELOADER] ⚠️ Alcuni dati non sono stati precaricati, ma il sistema continuerà a funzionare');
        }
      } catch (error) {
        console.error('[CLIENT PRELOADER] ❌ Errore durante il preloading:', error);
      }
    };

    // Avvia il preloading con un piccolo delay per non bloccare il rendering iniziale
    const timeoutId = setTimeout(preloadData, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  return {
    isPreloading: !hasPreloaded.current,
    status: preloadingStatus.current
  };
};

/**
 * Hook semplificato per il preloading automatico
 * 
 * Questo hook può essere utilizzato nei componenti principali
 * per attivare automaticamente il preloading senza dover
 * gestire manualmente lo stato.
 */
export const useAutoPreloader = () => {
  useMetalPricePreloader();
};
